<template>
  <v-row no-gutters>

    <v-dialog
      v-model="debtEditDialog"
      max-width="600"
    >
      <v-card>
        <v-card-title>
          Enter Debt
        </v-card-title>
        <v-container fluid>
          <v-row>
            <v-col cols="9">
              <v-text-field
                v-model="debtEditing.name"
                outlined
                label="Name"
                hide-details
                dense
              />
            </v-col>
            <v-col cols="5">
              <v-text-field
                v-model="debtEditing.balance"
                outlined
                label="Balance"
                hide-details
                dense
              />
            </v-col>
            <v-col cols="4">
              <v-text-field
                v-model="debtEditing.minimum_payment"
                outlined
                label="Minimum Payment"
                hide-details
                dense
              />
            </v-col>
            <v-col cols="3">
              <v-text-field
                v-model="debtEditing.interest_rate"
                outlined
                label="Interest Rate"
                hide-details
                dense
              />
            </v-col>
          </v-row>
        </v-container>
        <v-card-actions>
          <v-spacer />
          <v-btn color="secondary" @click="debtEditDialog = false">Cancel</v-btn>
          <v-btn color="primary" @click="saveDebt"><v-icon class="mr-2">mdi-content-save</v-icon>Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-col cols="12" style="position: relative;">
      <div class="title">Options</div>
      <v-row>
        <v-col cols="4">
          <v-select
            v-model="strategy"
            outlined
            label="Snowball Strategy"
            @change="recalculatePaymentSchedule"
            :items="strategyItems"
          />
        </v-col>
        <v-col cols="4" v-show="strategy != 'min'">
          <v-text-field
            v-model="additionalPrincipalField"
            type="number"
            outlined
            label="Additional Principal"
            placeholder="$"
            @change="updateAdditionalPrincipal()"
          />
        </v-col> 
      </v-row>
    </v-col>
    <v-col cols="12" style="position: relative;">
      <div class="title">Overview</div>
      <v-row>
        <v-col cols="3">
          <v-card style="height: 100%;">
            <v-card-text>
              <div class="overline text-center">Initial Balance</div>
              <div class="title text-center">{{ totalBalance | currencyRounded }}</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="3">
          <v-card style="height: 100%;">
            <v-card-text>
              <div class="overline text-center">Monthly Payment</div>
              <div class="title text-center">{{ totalMinPayment + $root.getNumber(additionalPrincipal) | currencyRounded }}</div>
              <!--<table class="debtSnowballOverviewCell">
                <tr>
                  <td class="text-right overline">Min</td>
                  <td class="px-2 currency">{{ totalMinPayment | numberRounded }}</td>
                </tr>
                <tr>
                  <td class="text-right overline">Addl</td>
                  <td class="px-2 currency">{{ $root.getNumber(additionalPrincipal) | numberRounded }}</td>
                </tr>  
                <tr>
                  <td class="text-right overline"><b>Total</b></td>
                  <td class="px-2 currency"><b>{{ totalMinPayment + $root.getNumber(additionalPrincipal) | numberRounded }}</b></td>
                </tr>
              </table>-->
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="3">
          <v-card style="height: 100%;">
            <v-card-text>
              <div class="overline text-center">Interest Paid</div>
              <div class="title text-center">{{ totalInterestPaid | currencyRounded }}</div>
              <!--<table class="debtSnowballOverviewCell">
                <tr>
                  <td class="text-right overline">PRIN</td>
                  <td class="px-2 currency">{{ totalBalance | numberRounded }}</td>
                </tr>
                <tr>
                  <td class="text-right overline">INT</td>
                  <td class="px-2 currency">{{ totalInterestPaid | numberRounded }}</td>
                </tr>  
                <tr>
                  <td class="text-right overline"><b>TOTAL</b></td>
                  <td class="px-2 currency"><b>{{ totalBalance + totalInterestPaid | numberRounded }}</b></td>
                </tr>
              </table>-->
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="3">
          <v-card style="height: 100%;">
            <v-card-text>
              <div class="overline text-center">Debt Free By</div>
              <div class="title text-center">{{ $moment().add((paymentSchedule.length ? paymentSchedule.length - 1 : 0), 'month').format('MMMM YYYY') }}</div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-col>
    <v-col cols="12" style="position: relative;">
      <div class="title">Debts</div>

      <draggable 
        class="row flex-column no-gutters" 
        v-model="debts" 
        group="debts" 
        @start="drag=true" 
        @end="drag=false" 
        handle=".drag-handle"
      >
        <v-col
          v-for="(debt, dIndex) in debts"
          :key="'debt' + dIndex"
          class="mb-2"
        >
          <v-card>
            <v-row no-gutters class="px-2 align-center">
              <v-col class="py-3 px-1 flex-grow-0 flex-shrink-1" style="min-width: 50px;">
                <v-icon class="drag-handle" v-show="strategy == 'custom'">mdi-drag</v-icon>
              </v-col>
              <v-col>
                <v-row no-gutters class="flex-nowrap">
                  <v-col class="py-1 px-1">
                    <div class="overline grey--text">Name</div>
                    <div class="subtitle-1">{{ debt.name }}</div>
                  </v-col>
                  <v-col class="py-1 px-1">
                    <div class="overline grey--text">Balance</div>
                    <div class="subtitle-1">{{ debt.balance | currencyRounded }}</div>
                  </v-col>
                  <v-col class="py-1 px-1">
                    <div class="overline grey--text">APR</div>
                    <div class="subtitle-1">{{ debt.interest_rate }}%</div>
                  </v-col>
                  <v-col class="py-1 px-1">
                    <div class="overline grey--text">Min Payment</div>
                    <div class="subtitle-1">{{ debt.minimum_payment | currencyRounded }}</div>
                  </v-col>
                </v-row>
              </v-col>
              <v-col class="flex-shrink-1 flex-grow-0" style="min-width: 300px;">
                <v-row no-gutters class="flex-nowrap green lighten-5">
                  <v-col class="py-1 px-2 text-no-wrap text-center">
                    <div class="overline grey--text text-no-wrap">Total Interest</div>
                    <div class="subtitle-1 text-no-wrap">{{ debt.totalInterest | currencyRounded }}</div>
                  </v-col>
                  <v-col class="py-1 px-2 text-no-wrap text-center">
                    <div class="overline grey--text text-no-wrap">Payoff</div>
                    <div class="subtitle-1 text-no-wrap">{{ $moment().add( debt.payoffMonths, 'month').format('MMMM YYYY') }}</div>
                  </v-col>
                </v-row>
              </v-col>
              <v-col class="px-1 py-3 flex-grow-0 flex-shrink-1 text-no-wrap">
                <v-btn color="accent" icon small @click="editDebt(debt.id)"><v-icon small>mdi-pencil</v-icon></v-btn>
                <v-btn color="error" icon small @click="removeDebt(debt.id)"><v-icon small>mdi-delete</v-icon></v-btn>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col class="text-center grey lighten-4 py-1">
                <v-btn x-small text @click="$set(showDebtDetails, dIndex, 0)" v-if="showDebtDetails[ dIndex ] == 1">Hide Details</v-btn>
                <v-btn x-small text @click="$set(showDebtDetails, dIndex, 1)" v-else>View Details</v-btn>
              </v-col>
            </v-row>
            <v-row v-if="showDebtDetails[ dIndex ] == 1">
              <v-col>
                <v-simple-table dense>
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th>Month</th>
                        <th>Starting Balance</th>
                        <th>Total Payment</th>
                        <th>Interest Paid</th>
                        <th>Principal Paid</th>
                        <th>Additional Principal</th>
                        <th>Ending Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(row,index) in getPaymentData(debt.id)" :key="debt.name + '-row-' + index">
                        <td>{{ $moment().add(index, 'month').format('YYYY-MM') }}</td>
                        <td>{{ row.startingBalance | currencyRounded }}</td>
                        <td>{{ row.paymentAmount | currencyRounded }}</td>
                        <td>{{ row.interestPaid | currencyRounded }}</td>
                        <td>{{ row.principalPaid | currencyRounded }}</td>
                        <td>{{ row.addlPrincipal | currencyRounded }}</td>
                        <td>{{ row.endingBalance | currencyRounded }}</td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
       
      </draggable>
      <div class="text-center mt-4">
        <v-btn color="primary" small @click="addDebt"><v-icon small class="mr-2">mdi-plus</v-icon>Add a Debt</v-btn>
      </div>

    </v-col>
  </v-row>
</template>

<script>
import Draggable from 'vuedraggable'

export default {
  name: 'ToolDebtSnowball',
  components: {
    'draggable': Draggable
  },
  data: () => ({
    drag: false,
    strategy: 'min',
    strategyItems: [
      {
        text: 'None (Minimum Payment Only)',
        value: 'min'
      },
      {
        text: 'Lowest Balance First',
        value: 'balance-low'
      },
      {
        text: 'Highest Balance First',
        value: 'balance-high'
      },
      {
        text: 'Lowest Interest Rate',
        value: 'apr-low'
      },
      {
        text: 'Highest Interest Rate',
        value: 'apr-high'
      },
      {
        text: 'Custom Order',
        value: 'custom'
      }
    ],
    debtEditDialog: false,
    debtEditing: {},
    additionalPrincipalField: null,
    additionalPrincipal: null,
    debts: [],
    paymentSchedule: [],
    totalBalance: 0,
    totalInterestPaid: 0,
    totalMinPayment: 0,

    showDebtDetails: []
  }),
  watch: {
    /*debts: {
      handler() {
        this.recalculatePaymentSchedule()
      },
      deep: true
    },*/
    additionalPrincipal() {
      this.recalculatePaymentSchedule()
    },
    drag(newVal) {
      if(newVal) this.showDebtDetails = []
      if(!newVal) this.recalculatePaymentSchedule()
    },
    strategy(newVal) {
      if(newVal == 'min') {
        this.additionalPrincipalField = null;
        this.additionalPrincipal = 0;
      } else if(newVal != 'custom') {
        this.reSortDebts()
      }
    }
  },
  computed: {
    
  },
  methods: {
    updateAdditionalPrincipal() {
      console.log('METHOD: updateAdditionalPrincipal');
      this.additionalPrincipal = this.$root.getNumber(this.additionalPrincipalField)
    },
    recalculateDebts() {
      console.log('METHOD: recalculateDebts');

      let totalBalance      = 0
      let totalMinPayment   = 0

      for(let i = 0; i < this.debts.length; i++) {
        totalBalance    += Math.round(this.debts[i].balance * 100)
        totalMinPayment += Math.round(this.debts[i].minimum_payment * 100)
      }

      this.totalBalance     = totalBalance / 100
      this.totalMinPayment  = totalMinPayment / 100
    },
    recalculatePaymentSchedule() {
      console.log('METHOD: recalculatePaymentSchedule')
      this.paymentSchedule = this.calculatePaymentSchedule((this.strategy == 'min' ? true : false))

      let totalInterest   = 0

      let debtIndexMap = {}
      let debtCalculations = {}
      for(let i = 0; i < this.debts.length; i++) {
        debtIndexMap[ this.debts[i].id ] = i
        debtCalculations[ this.debts[i].id ] = {
          totalInterest: 0,
          payoffMonths: 0
        }
      }

      for(let months = 0; months < this.paymentSchedule.length; months++) {
        let schedule = this.paymentSchedule[months]
        for(let i = 0; i < schedule.length; i++) {
          let entry = schedule[i]
          if(entry.startingBalance) {
            let interest = Math.round(entry.interestPaid * 100)
            debtCalculations[ entry.id ].payoffMonths   = months
            debtCalculations[ entry.id ].totalInterest += interest
          }
        }
      }

      for(let i = 0; i < this.debts.length; i++) {
        let debt = this.debts[i]
        let interest = debtCalculations[ debt.id ].totalInterest
        let payoffMonths  = debtCalculations[ debt.id ].payoffMonths
        this.$set(this.debts[i], 'totalInterest', interest / 100)
        this.$set(this.debts[i], 'payoffMonths', payoffMonths)
        totalInterest += interest
      }
      this.totalInterestPaid  = totalInterest / 100
    },
    calculatePaymentSchedule(minOnly) {
      console.log('METHOD: calculatePaymentSchedule', minOnly);
      this.recalculateDebts()
      
      let schedule = [];
      let balance = Math.round(this.totalBalance * 100)

      //Failsafe to prevent infinite loop due to floating point errors
      let breakpoint = 0;
      while(balance > 0 && breakpoint < 1000) {
        let scheduleEntry = []
        let availablePayment = Math.round(this.totalMinPayment * 100)
        availablePayment += this.$root.getNumber((this.additionalPrincipal * 100))
        availablePayment = Math.round(availablePayment)

        //Apply every debts minimum payment
        for(let i = 0; i < this.debts.length; i++) {
          let debt = this.debts[i]
          let paymentAmount   = Math.round(debt.minimum_payment * 100)
          let startingBalance = (schedule.length ? Math.round(schedule[ schedule.length - 1 ][i].endingBalance * 100) : Math.round(debt.balance * 100) )
          let interestRate    = debt.interest_rate / 1200
          let interestAmount  = 0
          let principalAmount = 0
          let overpayment     = 0
          let endingBalance   = startingBalance

          interestAmount      = Math.round(startingBalance * interestRate)
          principalAmount     = paymentAmount - interestAmount

          if(principalAmount > startingBalance) overpayment = principalAmount - startingBalance

          paymentAmount       -= overpayment
          principalAmount     -= overpayment
          endingBalance       = startingBalance - principalAmount

          availablePayment  -= paymentAmount
         
          let entry = {
            id:               debt.id,
            name:             debt.name,
            startingBalance:  Math.round(startingBalance) / 100,
            endingBalance:    Math.round(endingBalance) / 100,
            paymentAmount:    Math.round(paymentAmount) / 100,
            principalPaid:    Math.round(principalAmount) / 100,
            addlPrincipal:    0,
            interestPaid:     Math.round(interestAmount) / 100
          }

          scheduleEntry[i] = entry
          balance = balance - Math.round(principalAmount)
        }

        //Only do this if minOnly is false
        if(!minOnly) {
          //Apply additional principle to the loans in order
          for(let i = 0; i < scheduleEntry.length; i++) {
            //Skip iterations if no remaining available payment
            if(availablePayment <= 0) break

            let entry = scheduleEntry[i]
            let endingBalance   = Math.round(entry.endingBalance * 100)
            let paymentAmount   = Math.round(entry.paymentAmount * 100)
            let additional      = availablePayment
            
            //If the entry is already payed off, skip it
            if(endingBalance == 0) continue;
            //If available extra money > balance, only apply the amount needed
            if(additional > endingBalance) additional = endingBalance
            
            scheduleEntry[i].addlPrincipal    = Math.round(additional) / 100
            scheduleEntry[i].endingBalance    = Math.round(endingBalance - additional) / 100
            scheduleEntry[i].paymentAmount    = Math.round(paymentAmount + additional) / 100
            availablePayment                  -= additional
            balance                           -= Math.round(additional)
          }
        }

        schedule.push(scheduleEntry)
        breakpoint++
      }
      

      return schedule
    },
    sortDebts(strat) {
      console.log('METHOD: sortDebts', strat);
      let debtArr = this.debts.sort((a,b) => {
        switch(strat) {
          case 'balance-low':
            if(a.balance < b.balance) return -1
            if(a.balance > b.balance) return 1
            return 0
          case 'balance-high':
            if(a.balance > b.balance) return -1
            if(a.balance < b.balance) return 1
            return 0
          case 'apr-low':
            if(a.interest_rate < b.interest_rate) return -1
            if(a.interest_rate > b.interest_rate) return 1
            return 0
          case 'apr-high':
            if(a.interest_rate > b.interest_rate) return -1
            if(a.interest_rate < b.interest_rate) return 1
            return 0
          default: 
            return 0
        }
      })
      return debtArr
    },
    reSortDebts() {
      console.log('METHOD: reSortDebts');
      //if(this.strategy == 'min' || this.strategy == 'custom') return;
      this.debts = this.sortDebts(this.strategy)
      this.recalculatePaymentSchedule()
    },
    getPaymentData(id) {
      console.log('METHOD: getPaymentData', id);
      let arr = []
      for(let i = 0; i < this.paymentSchedule.length; i++) {
        let entry = null
        for(let i2 = 0; i2 < this.paymentSchedule[i].length; i2++) {
          if(this.paymentSchedule[i][i2].id == id) entry = this.paymentSchedule[i][i2]
        }
        if(entry) {
          if(entry.startingBalance == 0) break
          arr.push( entry )
        }
      }
      return arr
    },
    getDebtTotalInterest(id) {
      console.log('METHOD: getDebtTotalInterest', id);
      let total = 0
      for(let i = 0; i < this.paymentSchedule.length; i++) {
        for(let i2 = 0; i2 < this.paymentSchedule[i].length; i2++) {
          if(this.paymentSchedule[i][i2].id == id) total += this.paymentSchedule[i][i2].interestPaid
        }
      }
      return total
    },
    addDebt() {
      console.log('METHOD: addDebt');
      this.debtEditing = {
        id: Date.now()
      }
      this.debtEditDialog = true
      //this.debts.push({})
    },
    editDebt(id) {
      console.log('METHOD: editDebt', id);
      for(let i = 0; i < this.debts.length; i++) {
        if(this.debts[i].id == id) {
          this.debtEditing = JSON.parse(JSON.stringify(this.debts[i]))
          this.debtEditDialog = true
          break
        }
      }
    },

    saveDebt() {
      console.log('METHOD: saveDebt');
      let data = JSON.parse(JSON.stringify(this.debtEditing))
      data.balance = this.$root.getNumber(data.balance)
      data.minimum_payment = this.$root.getNumber(data.minimum_payment)
      data.interest_rate = this.$root.getNumber(data.interest_rate)

      let exists = null
      for(let i = 0; i < this.debts.length; i++) {
        if(this.debts[i].id == data.id) exists = i
      }
      if(exists === null) {
        this.debts.push(data)
      } else {
        this.$set(this.debts, exists, data)
      }
      this.reSortDebts()
      this.debtEditDialog = false
    },
    removeDebt(id) {
      console.log('METHOD: removeDebt', id);
      this.$swal.fire({
        title: 'Confirm Removal',
        html: 'Are you sure you want to remove this debt?',
        confirmText: 'Yes',
        cancelText: 'No'
      }).then(result => {
        if(result.value) {
          for(let i = 0; i < this.debts.length; i++) {
            if(this.debts[i].id == id) {
              this.debts.splice(i, 1)
              break
            }
          }
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
  .drag-handle {
    cursor: move;
  }
</style>